import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PartsStatusDropdowns, PartsStatusValues, VehiclePipelineStage } from "../../models/Vehicle";
import useVehicleStats from "../../data/useVehicleStats";
import DashboardStat from "../dashboard/DashboardStat";
import DashboardTileMultiple from "../dashboard/DashboardTileMultiple";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import CollapsibleSection from "../shared/CollapsibleSection";
import classNames from "../../utils/classNames";

const STORAGE_KEY = "wilma:vehicles:statsCollapsed";

function readStoredCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function CompactStatChip({
  label,
  value,
  color,
}: {
  label: string;
  value: string | number;
  color: string;
}) {
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs whitespace-nowrap",
        `bg-${color}-100 text-${color}-700`
      )}
    >
      <span className="font-semibold tabular-nums">{value}</span>
      <span className="text-[10px] font-medium opacity-90">{label}</span>
    </span>
  );
}

export default function VehicleStats() {
  const { t } = useTranslation("vehicles");
  const { stats } = useVehicleStats();
  const hasTmeFeature = CheckFeatureFlag("TME");
  const [collapsed, setCollapsed] = useState(readStoredCollapsed);

  const partsStatus = [
    ...Object.values(PartsStatusDropdowns).filter((ct) => isNaN(Number(ct))),
  ];

  const pipelineStages = [
    ...Object.values(VehiclePipelineStage).filter((ct) => isNaN(Number(ct))),
  ] as (keyof typeof VehiclePipelineStage)[];

  const pipelineStageLabels: Record<keyof typeof VehiclePipelineStage, string> = {
    B2B: t("B2B"),
    Targeting: t("Targeting"),
    NotOnSite: t("Purchased"),
    PreTriage: t("Pre Triage"),
    Stored: t("Stored"),
    AwaitingDismantling: t("Awaiting Dismantling"),
    Dismantling: t("Dismantling"),
    Dismantled: t("Dismantled"),
    Complete: t("Complete"),
    Archived: t("Archived"),
  };

  const memoStats = useMemo(() => {
    if (stats.isLoading || !stats.data) return;
    return stats.data.data;
  }, [stats.isLoading, stats.data]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // Ignore storage failures; preference still applies for the current session.
    }
  }, [collapsed]);

  const compactKpis = [
    { label: t("Schedule"), value: memoStats?.awaitingInventory ?? 0, color: "emerald" },
    { label: t("Dismantling"), value: memoStats?.awaitingDismantling ?? 0, color: "lime" },
    { label: t("COD"), value: memoStats?.awaitingCOD ?? 0, color: "slate" },
    { label: t("S&D"), value: memoStats?.startsAndDrives ?? 0, color: "purple" },
    { label: t("Added today"), value: memoStats?.addedToday ?? 0, color: "blue" },
    { label: t("Collection"), value: memoStats?.awaitingCollection ?? 0, color: "amber" },
    { label: t("Collection today"), value: memoStats?.awaitingCollectionToday ?? 0, color: "amber" },
    { label: t("Collected today"), value: memoStats?.collectedToday ?? 0, color: "green" },
    { label: t("Drop off"), value: memoStats?.awaitingDropOff ?? 0, color: "rose" },
    { label: t("Drop off today"), value: memoStats?.awaitingDropOffToday ?? 0, color: "rose" },
  ];

  return (
    <div className="mt-4 mb-4 bg-white shadow rounded overflow-hidden">
      <div
        className={classNames(
          "flex items-center gap-3 px-4 py-2",
          !collapsed && "border-b border-gray-100"
        )}
      >
        <span className="text-sm font-semibold text-gray-700 shrink-0">{t("Vehicle stats")}</span>

        {collapsed && (
          <div className="flex-1 min-w-0 overflow-x-auto">
            <div className="flex items-center gap-2 w-max pr-2">
              {compactKpis.map((kpi) => (
                <CompactStatChip key={kpi.label} label={kpi.label} value={kpi.value} color={kpi.color} />
              ))}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => setCollapsed((prev) => !prev)}
          className="ml-auto px-2.5 py-1 rounded border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 shrink-0"
        >
          {collapsed ? t("Show") : t("Hide")}
        </button>
      </div>

      <CollapsibleSection isOpen={!collapsed}>
        <div className="p-4 grid gap-4 grid-cols-1 grid-rows-1 sm:grid-rows-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-5">

      {!hasTmeFeature && (
        <div className="row-span-2 h-full rounded bg-teal-100 text-teal-600 p-2 px-4 prose-base">
          <h1 className="mb-1 font-semibold">{t("Parts Status")}</h1>
          <ul className="p-0 text-sm">
            {partsStatus.map((status, i) => (
              //@ts-ignore
              <li key={i} className="!m-0">
                {status}:{" "}
                {
                  memoStats?.engineExportCounts?.find(
                    (count) =>
                      count.status ===
                      //@ts-ignore
                      PartsStatusValues[status]
                  )?.count
                }
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasTmeFeature && (
        <div className="row-span-2 h-full rounded bg-teal-100 text-teal-600 p-2 px-4 prose-base">
          <h1 className="mb-1 font-semibold">{t("Pipeline")}</h1>
          <ul className="p-0 text-sm">
            {pipelineStages.map((stage, i) => (
              <li key={i} className="!m-0">
                {pipelineStageLabels[stage]}:{" "}
                {memoStats?.pipelineCounts?.find(
                  (count) => count.stage === VehiclePipelineStage[stage]
                )?.count ?? 0}
              </li>
            ))}
          </ul>
        </div>
      )}

      <DashboardStat
        title={memoStats?.awaitingInventory.toString() ?? "0"}
        desc={t("Awaiting Schedule")}
        color="emerald"
      />

      <DashboardStat
        title={memoStats?.awaitingDismantling.toString() ?? "0"}
        desc={t("Awaiting Dismantling")}
        color="lime"
      />

      <DashboardStat
        title={memoStats?.awaitingCOD.toString() ?? "0"}
        desc={t("Awaiting COD")}
        color="slate"
      />

      <DashboardStat
        title={memoStats?.startsAndDrives.toString() ?? "0"}
        desc={t("Starts & Drives")}
        color="purple"
      />

      <DashboardStat
        title={memoStats?.addedToday.toString() ?? "0"}
        desc={t("Added Today")}
        color="blue"
      />

      <DashboardTileMultiple
        colour="amber"
        values={[
          memoStats?.awaitingCollection?.toString()      ?? "0",
          memoStats?.awaitingCollectionToday?.toString() ?? "0",
        ]}
        labels={[
          t("Awaiting Collection"),
          t("Awaiting Collection"),
        ]}
        subLabels={[t("All"), t("Today")]}
        borderless={true}
        // className={spanClass}
      />

      <DashboardStat
        title={memoStats?.collectedToday.toString() ?? "0"}
        desc={t("Collected Today")}
        color="green"
      />

      <DashboardTileMultiple
        colour="rose"
        values={[
          memoStats?.awaitingDropOff?.toString()      ?? "0",
          memoStats?.awaitingDropOffToday?.toString() ?? "0",
        ]}
        labels={[
          t("Awaiting Drop Off"),
          t("Awaiting Drop Off"),
        ]}
        subLabels={[t("All"), t("Today")]}
        borderless={true}
        // className={spanClass}
      />

        </div>
      </CollapsibleSection>
    </div>
  );
}
